import { computedFrom } from "aurelia-binding";
import * as Enumerable from "linq";
import { MonthYearType } from "../../views/components/comp-month-picker/comp-month-picker";
import { CustomGridPageBase } from "../../views/pages/common/custom-grid-page.base";
import { MepLookupFiltersHandler } from "../../views/pages/modules/mep/common-vm/mep-lookup-filters-handler";

export interface BaseMepResponse {
    hasError: boolean;
    error: string;
}

export class MepLookupItem {
    id: number;
    name: string;
    displayName: string;
    additionalData: { [key: string]: any };
    isDefault: boolean;

    public static getIdsFromLookups(items: MepLookupItem[], selectedItems: MepLookupItem[]): number[] {
        //remove the selectedItems.length != items.length as it will cause issue when we select all items
        return selectedItems ? Enumerable.from(selectedItems).select(x => x.id).toArray() : [];
        //disabled this since if we return empty the UI would not be able to show the selected items for cehckbox list 
        //return items && selectedItems && selectedItems.length != items.length ? Enumerable.from(selectedItems).select(x => x.id).toArray() : [];
    }

    public static getSelectedItems(items: MepLookupItem[], itemIds: number[]): MepLookupItem[] {
        return Enumerable.from(items).where(x => itemIds.indexOf(x.id) > -1).toArray();
    }
}

export interface MepGetLookupValuesRequest {
    portId?: number;
    years?: number[];
    customerId?: number;
    emissionTypesIncludeAllYears: boolean;
    queries: string[];
}

export interface MepGetLookupValuesResponse extends BaseMepResponse {
    allValuesByQuery: { [key: string]: MepLookupItem[]; };
}


export interface MepPortMenuItem {
    portId: number;
    portName: string;
    portImageUrl: string;
    showPortName: boolean;
    hasMultipleFyYears: boolean;
    portUnlocode: string;
    supportsCargoType: string;
}

export interface MepGetMonthsListResponse {
    items: MepMonthItem[];
}

export interface MepMonthItem {
    monthNo: number;
    calendarMonthNo: number;
    year: number;
    monthName: string;
}

export interface MepGetVesselGroupsResponse extends BaseMepResponse {
    items: MepGetVesselGroupItemResponse[];
}

export interface MepGetVesselGroupItemResponse {
    vesselTypeGroupId: number;
    vesselTypeGroupName: string;
    vesselTypeGroupDescription: string;
}

export interface MepPortDetails {
    portId: number;
    portName: string;
    polygon: any;
    polygonGeoJson: string;
    portDescription: string;
    mepMethodologyUrl: string;
    mepAssumptionsUrl: string;
    npiMethodologyUrl: string;
    termsAndConditionUrl: string;
    initialForecastYear: number;
    portStudyAreaName: string;
    portImageUrl: string;
    showPortName: boolean;
    pointsOfInterest: MepPointsOfInterest[];
    supportsCargoType: boolean;
}

export interface MepPointsOfInterest {
    pointOfInterestPolygonId?: number;
    pointOfInterestName: string;
    pointOfInterestGroupName: string;
    polygon: any;
    pointOfInterestPolygon: string;
    portId: number;
}

export interface MepDashboardSummaryResponse extends BaseMepResponse {
    totalEmissionsPerType: MepDashboardTotalEmissions[];
    totalEmissionsByScope: MepDashboardTotalEmissionsByScope[];
}

export interface MepGetDashboardVesselStatisticsRequest extends MepDashboardRequest {
    lastYear?: number;
}

export interface MepGetDashboardVesselStatisticsResponse {
    vesselStatistics: MepDashboardVesselStatistics;
    vesselCallsPercentage: number;
    vesselCountPercentage: number;
    tugboatJobsPercentage: number;
}

export interface MepDashboardVesselStatistics {
    vesselCalls: number;
    vesselCount: number;
    tugboatJobs: number;
    totalVesselCalls: number;
    totalVesselCount: number;
    totalTugboatJobs: number;
}

export interface MepDashboardTotalEmissions extends BaseMepEmissionBySomeType {
    emissionsTypeFullName: string;
    emissionTypeCategoryId: number;
    weightingFactor: number;
}

export interface MepDashboardTotalEmissionsByScope {
    scopeId: number;
    weightedEmissionValue: number;
    emissionTypeCategoryId: number;
}

export interface BaseMepRequest {
    portId: number;
    pointOfInterestIds: number[];
    vesselTypeIds: number[];
    vesselTypeGroupIds: number[];
    operationModeIds: number[];
    cargoTypeIds: number[];
    year: number;
    months?: number[];
    step?: number;
}

export interface GetMepEmissionsAdditionalDetailsRequest extends BaseMepRequest {
    emissionTypeId: number;
}

export interface MepDashboardRequest extends BaseMepRequest {
    emissionTypeIds: number[];
}

export interface MepUploadVesselPortActivitiesRequest {
    file: File;
    unPortCode: string;
}

export interface MepUpdateCargoDataRequest {
    portId: number;
    year: number;
    activity_MemberId: number;
    cargoType: string;
    cargoQuantity: number;
}

export interface MepGetDashboardVesselTypeDistributionRequest extends MepDashboardRequest {
    vesselStatisticTypeId: number;
}

export interface MepGetDashboardEmissionsBySomeTypeRequest extends MepDashboardRequest {
    secondaryTypeId: number;
}

export interface MepGetPointsOfInterestHeatmapRequest extends MepDashboardRequest {
    zoomLevel: number;
    requestId: string;
}

export interface MepTopVesselsEmissionsPerformers {
    orderNo: number;
    emissionValueTonnes: number;
    vesselId: number;
    vesselName: string;
    imo: number;
    hashColour: string;
    emissionTypeCategoryId: number;
}

export interface GetMepEmissionsByMonthResponse extends BaseMepResponse {
    emissionsByMonth: MepEmissionsByMonth[];
    insightsInformation: MepEmissionsByMonthInsightsInformation;
}

export interface MepEmissionsByMonth {
    emissionMonth: number;
    emissionMonthName: string;
    emissionYear: number;
    calendarYear: number;
    emissionTypeCategoryId: number;
    totalEmissionsQtyTonnes: number;
}

export interface BaseMepEmissionBy {
    totalEmissionsQtyTonnes: number;
    emissionsTypeName: string;
    emissionsTypeId: number;
}

export interface MepEmissionsByMonthInsightsInformation {
    emissionsPercentage: number;
    months: string[];
    year: number;
}

export interface MepGetEmissionsBySomeTypeResponse {
    results: MepDashboardEmissionsBySomeTypeItem[];
}

export interface BaseMepEmissionBySomeType extends BaseMepEmissionBy {
    totalEmissionsQtyTonnes: number;
    emissionsTypeId: number;
    emissionsTypeName: string;
    displayOrder: number;
    colourCode: number;
    targetEmissionsQtyTonnes: number;
    emissionTypeCategoryId: number;
    weightingFactor: number;
}

export enum EmissionTypeCategoryEnum {
    GhgEmissions = 1,
    CriteriaPollutants = 2
}

export interface MepDashboardEmissionsBySomeTypeItem extends BaseMepEmissionBySomeType {
    secondaryTypeName: string;
    secondaryTypeId: number;
    secondaryTypeGroupName: string;
    totalEmissionsPercentage: number;
    smoothenedEmissionProportion: number;
    hashColour: string;
}

export interface MepEmissionsPerPortActivityPerHour {
    emissionValueInTonnes: number;
}
export interface MepEmissionColourDetails {
    colourCode: number;
    lowerValue: number;
    upperValue: number;
}
export interface GetMepEmissionsAdditionalDetailsResponse extends BaseMepResponse {
    mepEmissionsPerPortActivityPerHour: MepEmissionsPerPortActivityPerHour;
    colourDetails: MepEmissionColourDetails[];
}

export interface MepGetDashboardVesselCallsByVesselTypeResponse {
    result: MepDashboardVesselTypeDistributionResponseItem[];
}

export interface MepDashboardVesselTypeDistributionResponseItem {
    vesselTypeId: number;
    vesselTypeName: string;
    count: number;
    hashColour: string;
}

export interface MepGetDashboardPointsOfInterestHeatmapConfig {
    radius: number;
    zoomLevel: number;
    scaleRadius: boolean;
    minOpacity: number;
    maxOpacity: number;
    useLocalExtrema: boolean;
    max: number;
}

export interface MepGetDashboardPointsOfInterestHeatmapResponse extends BaseMepResponse {
    heatmapConfigs: MepGetDashboardPointsOfInterestHeatmapConfig[];
    results: MepDashboardPointOfInterestHeatmap[];
    requestId: string;
}

export interface MepDashboardPointOfInterestHeatmap {
    latitude: number;
    longitude: number;
    intensityValue: number;
}

export enum MepTabsEnum {
    Home = 1,
    Dashboard,
    Insights,
    Vessels,
    YoyComparison,
    EmissionTargets,
    ScenarioPlanning,
}

export enum MepHomeTabsEnum {
    PortsSummary = 1,
    About = 2,
    Terminology = 3
}

export class MepTabsConstants {
    public static Home = "home";
    public static Dashboard = "dashboard";
    public static Insights = "insights";
    public static Vessels = "vessels";
    public static YoyComparison = "yoy-comparison";
    public static EmissionTargets = "emission-targets";
    public static ScenarioPlanning = "scenario-planning";
}

export abstract class BaseMepFilterablePage<T extends BaseMepFilterViewModel> {
    public filterViewModel: T;

    abstract initializeFilterVM(portId?: number): T;
}

export abstract class BaseMepFilterableCustomGrdPage<T extends BaseMepFilterViewModel> extends CustomGridPageBase {
    public filterViewModel: T;

    abstract initializeFilterVM(moodel: any): T;
}

export abstract class BaseMepFilterViewModel {

    private _monthNames = {
        1: 'Jan',
        2: 'Feb',
        3: 'Mar',
        4: 'Apr',
        5: 'May',
        6: 'Jun',
        7: 'Jul',
        8: 'Aug',
        9: 'Sep',
        10: 'Oct',
        11: 'Nov',
        12: 'Dec'
    };

    protected lookupFiltersHandler: MepLookupFiltersHandler;

    @computedFrom("lookupFiltersHandler", "lookupFiltersHandler.selectedMonths", "lookupFiltersHandler.selectedYear")
    public get dateRangeLabel(): string {


        //If all months selected
        if (this.lookupFiltersHandler.selectedMonths == null || this.lookupFiltersHandler.selectedMonths.length == 0) {
            return "FY " + this.selectedYear;
        }
        //If months are not contiguous
        else if (this.selectedMonths.length > 1 && this.selectedMonths.some((x, idx) => idx < this.selectedMonths.length - 1 && ((x.month + 1) % 12) != this.selectedMonths[idx + 1].month % 12)) {
            const enumerable = Enumerable.from(this.selectedMonths)
                .orderBy(x => x.year)
                .thenBy(x => x.month);

            const months = enumerable
                .select(x => this._monthNames[x.month])
                .toArray()
                .join(", ");
            //ex: FY 2022 (Jul, Sep, Nov)
            return `FY ${this.selectedYear} (${months})`;
        }
        //If months are contiguous
        else {
            const enumerable = Enumerable.from(this.selectedMonths)
                .orderBy(x => x.year)
                .thenBy(x => x.month);
            const min = enumerable
                .firstOrDefault();
            const max = enumerable
                .lastOrDefault();
            if (max.month != min.month) {
                //ex: Jul 2021 - Sep 2021
                return `${this._monthNames[min.month]} ${min.year} - ${this._monthNames[max.month]} ${max.year}`;
            } else {
                return `${this._monthNames[min.month]} ${min.year}`;
            }
        }
    }

    @computedFrom("lookupFiltersHandler", "lookupFiltersHandler.selectedMonths", "lookupFiltersHandler.selectedYear")
    public get excelDateRangeLabel(): string {

        //If all months selected
        if (this.lookupFiltersHandler.selectedMonths == null || this.lookupFiltersHandler.selectedMonths.length == 0) {
            //ex: FY2020
            return `FY${this.selectedYear}`;
        }
        //If months are not contiguous
        else if (this.selectedMonths.length > 1 && this.selectedMonths.some((x, idx) => idx < this.selectedMonths.length - 1 && ((x.month + 1) % 12) != this.selectedMonths[idx + 1].month % 12)) {
            const enumerable = Enumerable.from(this.selectedMonths)
                .orderBy(x => x.year)
                .thenBy(x => x.month);
            const months = enumerable
                .select(x => this._monthNames[x.month])
                .toArray()
                .join(", ");
            //ex: FY2020 Jul, Sep, Nov
            return `FY${this.selectedYear}  ${months}`
        }
        //If months are contiguous
        else {
            const enumerable = Enumerable.from(this.selectedMonths)
                .orderBy(x => x.year)
                .thenBy(x => x.month);
            const min = enumerable
                .firstOrDefault();
            const max = enumerable
                .lastOrDefault();

            if (max.month != min.month) {
                //ex: FY2022 Jul 2021 - Sep 2021
                return `FY${this.selectedYear} ${this._monthNames[min.month]} ${min.year} - ${this._monthNames[max.month]} ${max.year}`;
            } else {
                return `FY${this.selectedYear} ${this._monthNames[min.month]}`;
            }
        }
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.selectedYear')
    get selectedMonths(): MonthYearType[] {
        return this.lookupFiltersHandler.selectedMonths;
    }

    set selectedMonths(value) {
        this.lookupFiltersHandler.selectedMonths = value;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.selectedYear')
    get selectedYear(): number {
        return this.lookupFiltersHandler.selectedYear;
    }
    set selectedYear(value) {
        this.lookupFiltersHandler.selectedYear = value;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.portId')
    get portId(): number {
        return this.lookupFiltersHandler ? this.lookupFiltersHandler.portId : null;
    }
    set portId(value) {
        if (this.lookupFiltersHandler)
            this.lookupFiltersHandler.portId = value;
    }

    abstract onSelectedMonthsChanged();
}

export interface MepCustomer {
    customerId: number;
    customerName: string;
    customerPlatformId: number;
}

export class MepEventMessages {
    public static ChangeMepTab = "change-mep-tab";
}

export interface MepPortMenuItemCheckbox extends MepPortMenuItem {
    isSelected?: boolean;
}

export interface MepLookupItemCheckbox extends MepLookupItem {
    isSelected?: boolean;
}

export interface DownloadEmissionsDataRequest {
    portIds: number[];
    years: number[];
}